import { AbstractDownloadableService } from "./abstract-downloadable.service";
import { CashDrawerTransaction } from "../models";

/**
 * @class CashDrawerTransactionPmsApiService
 * @abstract
 * @extends {AbstractDownloadableService<CashDrawerTransaction>}
 */
export class CashDrawerTransactionPmsApiService extends AbstractDownloadableService {

    /**
     * @type {string}
     * @protected
     */
    get urlPattern() {

        return '/cash-drawer/:cashDrawerId/cash-drawer-transaction(/:id)';
    }

    /**
     * @param {Object.<string, *>} attributes
     * @returns {CashDrawerTransaction}
     * @protected
     */
    newModel(attributes, ...params) {

        return new CashDrawerTransaction(attributes);
    }

    newTransactionCollection(paginationData, ...params) {

        return {
            total_row: paginationData.total_row,
            models: Object.values(paginationData.data).map(groups => {

                return {
                    'total_row': groups.total_row,
                    'items': groups.items.map(attributes => this.newModel(attributes, ...params))
                };
            }),
            current_page: paginationData.current_page,
            from: paginationData.from,
            to: paginationData.to,
            total: paginationData.total,
            last_page: paginationData.last_page,
        };
    }

    /**
     * @param {...*} params
     * @return {function(response: Object): PaginationCollection<M>}
     * @protected
     */
    getTransactionCollector(...params) {

        return (response) => {

            const responseData = this.extractData(response);

            if (responseData.data && Array.isArray(responseData.data)) {

                responseData.data = {
                    group: {
                        items: responseData.data
                    }
                };
            }

            return this.newTransactionCollection(responseData, ...params);
        };
    }

    /**
     * @param {Object=} params
     * @param {CashDrawer=} cashDrawer
     * @returns {Promise.<PaginationCollection<CashDrawerTransaction>>}
     */
    get(params = {}, cashDrawer = null) {

        const url = this.url({cashDrawerId: cashDrawer ? cashDrawer.id : 0});

        return this.http
            .get(url, {
                headers: this.authHeaders,
                params,
            })
            .then(this.getTransactionCollector())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {CashDrawerTransaction} cashDrawerTransaction
     * @param {cashDrawer} cashDrawer
     * @returns {Promise.<CashDrawerTransaction>}
     */
    create(cashDrawerTransaction, cashDrawer) {

        const url = this.url({cashDrawerId: cashDrawer.id});

        return this.http
            .post(url, cashDrawerTransaction.toObjectExcept('cash_drawer'), {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {CashDrawerTransaction} cashDrawerTransaction
     * @returns {Promise.<CashDrawerTransaction>}
     */
    update(cashDrawerTransaction) {

        const url = this.url({
            cashDrawerId: cashDrawerTransaction.cash_drawer.id,
            id: cashDrawerTransaction.id
        });

        return this.http
            .put(url, cashDrawerTransaction.toObjectExcept('cash_drawer'), {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {CashDrawerTransaction} cashDrawerTransaction
     * @returns {Promise.<boolean>}
     */
    delete(cashDrawerTransaction) {

        const url = this.url({
            cashDrawerId: cashDrawerTransaction.cash_drawer.id,
            id: cashDrawerTransaction.id
        });

        return this.http
            .delete(url, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    /**
     * 
     * @param {New Remove method} cashDrawer 
     * @param {*} params 
     * @returns 
     */
    remove(cashDrawerTransaction){
        const cashDrawerId = cashDrawerTransaction.cash_drawer.id;
        const customUrlPattern = '/cash-drawer/:cashDrawerId/remove-cash-transaction';
        const customUrl =  this.url({
            cashDrawerId: cashDrawerId
        },customUrlPattern)

        return this.http
            .post(customUrl, cashDrawerTransaction.toObjectExcept('cash_drawer'), {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    summary(cashDrawer, params = {}) {

        const url = this.url({
            cashDrawerId: cashDrawer.id
        }, '/cash-drawer/:cashDrawerId/summary');

        return this.http
            .get(url, {
                headers: this.authHeaders,
                params,
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    sendEmail(params = {}, cashDrawer) {

        const url = this.url({cashDrawerId: cashDrawer.id}) + '/email';

        return this.http
            .post(url, params, {
                headers: this.authHeaders,
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    exportPDF(params = {}, cashDrawer) {

        const url = this.url({cashDrawerId: cashDrawer.id}) + '/export-pdf';

        return this.download(url, params, 'Cash Drawer Transactions.pdf');
    }

    exportExcel(params = {}, cashDrawer) {

        const url = this.url({cashDrawerId: cashDrawer.id}) + '/export-excel';

        return this.download(url, params, 'Cash Drawer Transactions.xls');
    }

    exportCSV(params = {}, cashDrawer) {

        const url = this.url({cashDrawerId: cashDrawer.id}) + '/export-csv';

        return this.download(url, params, 'Cash Drawer Transactions.csv');
    }

    sendDrawerSummaryEmail(params = {}, cashDrawer) {

        const url = this.url({cashDrawerId: cashDrawer.id}) + '/drawer-summary-email';

        return this.http
            .post(url, params, {
                headers: this.authHeaders,
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }
}


// WEBPACK FOOTER //
// ./src/pms-api/services/cash-drawer-transaction.service.js